import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { PrismSelectInput } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  FORECAST_COLUMNS,
  type ForecastColumn,
} from 'lumen/utils/constants/market-snapshots-constants';

interface ForecastOption {
  label: string;
  value: ForecastColumn;
}

export interface MarketSnapshotsForecastSelectSignature {
  Args: {
    onChange: (forecastColumn: ForecastColumn) => void;
    value: ForecastColumn;
  };
}

export default class MarketSnapshotsForecastSelect extends Component<MarketSnapshotsForecastSelectSignature> {
  @service declare intl: Services['intl'];

  labelId = `forecast-select-label-${guidFor(this)}`;
  triggerId = `forecast-select-trigger-${guidFor(this)}`;

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get options(): ForecastOption[] {
    return [
      {
        label: this.intl.t('market.snapshots.forecast.bi'),
        value: FORECAST_COLUMNS.BI,
      },
      {
        label: this.intl.t('market.snapshots.forecast.user'),
        value: FORECAST_COLUMNS.USER,
      },
      {
        label: this.intl.t('market.snapshots.forecast.rms'),
        value: FORECAST_COLUMNS.RMS,
      },
      {
        label: this.intl.t('market.snapshots.forecast.finance'),
        value: FORECAST_COLUMNS.FINANCE,
      },
    ];
  }

  get selectedOption(): ForecastOption | undefined {
    return this.options.find((option) => option.value === this.args.value);
  }

  @action
  onChange(option: ForecastOption): void {
    this.args.onChange(option.value);
  }

  <template>
    <div class="flex items-center gap-200">
      <span
        class="text-200--regular text-neutral-subdued whitespace-nowrap"
        data-test-id="forecast-select-label"
        id={{this.labelId}}
      >
        {{t "general.forecast"}}
      </span>

      <PrismSelectInput
        @onChange={{this.onChange}}
        @options={{this.options}}
        @value={{this.selectedOption}}
        aria-labelledby="{{this.labelId}} {{this.triggerId}}"
        data-test-id="forecast-select"
        id={{this.triggerId}}
      />
    </div>
  </template>
}
